// @flow
import React, { useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import { filter, sortBy, groupBy } from 'lodash';
import AppointmentCard from './AppointmentCard';
import AppointmentSkeleton from './AppointmentSkeleton';

type Props = {
  appointments: Object[],
  selectedDate: Object,
  clinic: Object,
  clinicDoctors: Object,
  filter: Object,
  loadingItems: Object,
  isFinatialEnabled: boolean,
  user: Object,
  pending: boolean,
  failed: boolean,
  onShowAppointment: Function,
  onSelectDay: Function,
  _: Function,
};

const MonthView = ({
  appointments, selectedDate, clinic, clinicDoctors,
  filter: agendaFilter, loadingItems, isFinatialEnabled, user, pending, failed,
  onShowAppointment, onSelectDay, _,
}: Props) => {
  const [focusedDay, setFocusedDay] = useState(selectedDate.format('YYYY-MM-DD'));

  const todayStr = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const calendarGrid = useMemo(() => {
    const startOfMonth = selectedDate.clone().startOf('month');
    const endOfMonth = selectedDate.clone().endOf('month');
    const startOfGrid = startOfMonth.clone().startOf('isoWeek');
    const endOfGrid = endOfMonth.clone().endOf('isoWeek');

    const weeks = [];
    const current = startOfGrid.clone();

    while (current.isSameOrBefore(endOfGrid, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(current.clone());
        current.add(1, 'day');
      }
      weeks.push(week);
    }
    return weeks;
  }, [selectedDate]);

  const weekDayLabels = useMemo(() => {
    const start = moment().startOf('isoWeek');
    return Array.from({ length: 7 }, (__, i) => start.clone().add(i, 'days').format('ddd'));
  }, []);

  const appointmentsByDay = useMemo(
    () => groupBy(appointments, (apt) => moment.utc(apt.start).format('YYYY-MM-DD')),
    [appointments],
  );

  const focusedDayAppointments = useMemo(
    () => sortBy(
      filter(appointments, (apt) => moment.utc(apt.start).format('YYYY-MM-DD') === focusedDay),
      (apt) => moment.utc(apt.start).valueOf(),
    ),
    [appointments, focusedDay],
  );

  const handleDayClick = useCallback((day) => {
    const dayStr = day.format('YYYY-MM-DD');
    setFocusedDay(dayStr);
    onSelectDay(dayStr);
  }, [onSelectDay]);

  const isCurrentMonth = useCallback(
    (day) => day.month() === selectedDate.month(),
    [selectedDate],
  );

  return (
    <div className="android-month-view">
      <div className="month-calendar-grid">
        <div className="month-weekday-header">
          {weekDayLabels.map((label) => (
            <div key={label} className="weekday-label">{label}</div>
          ))}
        </div>
        {calendarGrid.map((week) => (
          <div key={week[0].format('YYYY-MM-DD')} className="month-week-row">
            {week.map((day) => {
              const dayStr = day.format('YYYY-MM-DD');
              const inMonth = isCurrentMonth(day);
              const isToday = dayStr === todayStr;
              const isFocused = dayStr === focusedDay;
              const hasAppts = (appointmentsByDay[dayStr] || []).length > 0;

              return (
                <div
                  key={dayStr}
                  className={
                    'month-day-cell' +
                    (!inMonth ? ' other-month' : '') +
                    (isToday ? ' is-today' : '') +
                    (isFocused ? ' is-focused' : '')
                  }
                  onClick={() => handleDayClick(day)}
                >
                  <span className="day-number">{day.format('D')}</span>
                  {hasAppts && <span className="day-dot" />}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="month-day-detail">
        <div className="month-detail-header">
          <span>{moment(focusedDay).format('dddd, DD/MM')}</span>
        </div>
        {focusedDayAppointments.length > 0 ? (
          <div className="month-detail-list">
            {focusedDayAppointments.map((apt) => (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                clinicDoctors={clinicDoctors}
                clinic={clinic}
                filter={agendaFilter}
                loadingItems={loadingItems}
                isFinatialEnabled={isFinatialEnabled}
                user={user}
                onClick={onShowAppointment}
                _={_}
              />
            ))}
          </div>
        ) : pending ? (
          <AppointmentSkeleton />
        ) : failed ? null : (
          <div className="month-detail-empty">
            <span>{_('label/no_appointments')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// $FlowFixMe
export default React.memo(MonthView);
